import { Chips, NumberInput, Separator, useTranslationContext } from 'platform/components';
import { HStack, VStack } from 'platform/foundation-tw';

// eslint-disable-next-line no-restricted-imports -- Intended
import { isNil, isNotNil, path } from 'ramda';
import { isString, toNumber } from 'ramda-adjunct';
import { suffixTestId } from 'shared/web';
import { useFilterOnChipsChange } from '../../hooks/useFilterOnChipsChange';
import type { EagFilterProps } from '../../types/EagFilterProps';
export type RangeFilterType = EagFilterProps & {
  quickFilters?: Array<{
    value: string;
    label: string;
  }>;
};
type FilterValue = {
  from: number | null;
  to: number | null;
} | string;
const FILTER_VALUE_KEY = 'value';
export function RangeFilter(props: RangeFilterType) {
  const t = useTranslationContext();
  const filterValue = props.value[FILTER_VALUE_KEY] as FilterValue | null;
  const setFilterValue = (value: FilterValue | null) => {
    props.onChange({
      [FILTER_VALUE_KEY]: value
    });
  };
  const {
    onChipsChange
  } = useFilterOnChipsChange({
    setFilterValue,
    defaultValue: {
      from: null,
      to: null
    }
  });
  const onFromInputChange = (value: number | null) => {
    if (typeof filterValue !== 'string' && isNil(value) && isNil((filterValue as {
      to: number | null;
    } | null)?.to)) {
      props.onChange({
        [FILTER_VALUE_KEY]: null
      });
      return;
    }
    if (isString(filterValue) || isNil(filterValue)) {
      setFilterValue({
        from: toNumber(value),
        to: null
      });
    }
    if (!isString(filterValue)) {
      setFilterValue({
        from: isNil(value) ? value : toNumber(value),
        to: (filterValue as {
          to: number | null;
        } | null)?.to === 0 ? null : (filterValue as {
          to: number | null;
        } | null)?.to ?? null
      });
    }
  };
  const onToInputChange = (value: number | null) => {
    const fromValue = (filterValue as {
      from: number | null;
    } | null)?.from;
    if (typeof filterValue !== 'string' && isNil(value) && (isNil(fromValue) || fromValue === 0)) {
      props.onChange({
        [FILTER_VALUE_KEY]: null
      });
      return;
    }
    if (isString(filterValue) || isNil(filterValue)) {
      setFilterValue({
        to: toNumber(value),
        from: null
      });
    }
    if (!isString(filterValue)) {
      setFilterValue({
        from: fromValue === 0 ? null : fromValue ?? null,
        to: isNil(value) ? value : toNumber(value)
      });
    }
  };
  const isQuickFilterValue = isString(filterValue) ? [filterValue] : null;
  return <VStack>
      {props.quickFilters && props.quickFilters.length > 0 && <>
          <HStack>
            <Chips isDisabled={props.isDisabled} value={isQuickFilterValue} options={props.quickFilters} onChange={onChipsChange} isMultiple={false} data-testid={suffixTestId('range-filter-quickFilters', props)} isDeselectable />
          </HStack>
          <Separator />
        </>}
      <HStack spacing={5}>
        <NumberInput label={t('general.labels.from')} value={hasValidKey(filterValue, 'from') ? toNumber(filterValue.from) : null} onChange={onFromInputChange} isDisabled={props.isDisabled} data-testid={suffixTestId('range-filter-from', props)} />
        <NumberInput label={t('general.labels.to')} value={hasValidKey(filterValue, 'to') ? toNumber(filterValue.to) : null} onChange={onToInputChange} isDisabled={props.isDisabled} data-testid={suffixTestId('range-filter-to', props)} />
      </HStack>
    </VStack>;
}
const hasValidKey = <T extends 'from' | 'to',>(val: unknown, key: T): val is Record<T, string> => isNotNil(path([key], val));